<template>
  <section class="development-page">
    <PageHeader :title="$t('general.my_development')" icon="uil-chart-growth"/>

    <div class="development-toolbar">
      <label class="development-search">
        <i aria-hidden="true" class="uil-search"></i>
        <span class="sr-only">{{ $t('general.search_competency') }}</span>
        <input v-model="searchText"
               :placeholder="$t('general.search_placeholder')"
               autocomplete="off"
               type="search"
        >
      </label>

      <div class="development-toolbar__controls">
        <multiple-select-input v-model="selectedSort"
                               :allowEmpty="false"
                               :multiple="false"
                               :required="false"
                               :searchable="false"
                               :showLabels="false"
                               :values="sortOptions"
                               class="development-sort"
                               @select="onSortChange"
        />

        <div :aria-label="$t('general.competencies_summary')" class="development-total">
          <span class="development-total__label">{{ $t('general.total_competencies') }}</span>
          <strong class="development-total__value">{{ loading ? "—" : totalCompetencies }}</strong>
        </div>
      </div>
    </div>

    <div v-if="loading" class="competency-grid" aria-busy="true" aria-live="polite">
      <span class="sr-only">{{ $t('general.loading_competencies') }}</span>
      <CompetencyCardSkeleton v-for="n in skeletonCount" :key="`skeleton-${n}`" />
    </div>

    <div v-else-if="errorMessage" class="development-state development-state--error" role="alert">
      <i aria-hidden="true" class="uil-exclamation-triangle"></i>
      <div>
        <strong>{{ $t('general.load_failed') }}</strong>
        <p>{{ errorMessage }}</p>
      </div>
      <button class="development-retry" type="button" @click="loadCompetencies">
        {{ $t('general.try_again') }}
      </button>
    </div>

    <div v-else-if="!competencies.length" class="development-state development-state--empty">
      <i aria-hidden="true" class="uil-award"></i>
      <div>
        <strong>{{ emptyTitle }}</strong>
        <p v-if="searchText.trim()">{{ $t('general.empty_search_hint') }}</p>
      </div>
    </div>

    <template v-else>
      <div class="competency-grid">
        <CompetencyCard v-for="assignment in competencies"
                        :key="assignment.id"
                        :assignment="assignment"
                        @select="openCompetency"
        />
      </div>

      <Pagination v-if="totalPages > 1"
                  :pagination="pagination"
                  class="development-pagination"
                  @changePage="changePage"
      />
    </template>
  </section>
</template>

<script>
import PageHeader from "@/view/components/common/PageHeader.vue";
import CompetencyCard from "@/view/components/developmentCompetencies/CompetencyCard.vue";
import CompetencyCardSkeleton from "@/view/components/developmentCompetencies/CompetencyCardSkeleton.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import Pagination from "@/assets/components/Pagination.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";
import widgetMixin from "@/core/mixin/widget.mixin";

export default {
  name: "MyDevelopment",
  mixins: [widgetMixin],
  components: {
    CompetencyCard,
    CompetencyCardSkeleton,
    MultipleSelectInput,
    PageHeader,
    Pagination,
  },
  data() {
    return {
      competencies: [],
      totalCompetencies: 0,
      searchText: "",
      selectedSort: null,
      page: 1,
      perPage: 15,
      totalPages: 1,
      loading: true,
      errorMessage: "",
      searchTimer: null,
      requestSequence: 0,
    };
  },
  computed: {
    skeletonCount() {
      return Math.min(this.perPage, 6);
    },
    sortOptions() {
      return [
        { id: "desc", name: this.$t("sorting.created_at_desc") },
        { id: "asc", name: this.$t("sorting.created_at_asc") },
      ];
    },
    pagination() {
      return {
        current_page: this.page,
        last_page: this.totalPages,
        per_page: this.perPage,
        total: this.totalCompetencies,
      };
    },
    emptyTitle() {
      return this.searchText.trim()
          ? this.$t("general.empty_search")
          : this.$t("general.empty_list");
    },
  },
  watch: {
    searchText() {
      clearTimeout(this.searchTimer);
      this.requestSequence += 1;
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.loadCompetencies();
      }, 350);
    },
    "$i18n.locale"() {
      this.syncSelectedSort();
    },
  },
  created() {
    this.syncSelectedSort();
  },
  async mounted() {
    // Widget kapalıysa sayfa erişime kapalı (sol menü ile aynı kural)
    try {
      await this.getWidgetsMixin();

      if (!this.isMyCompetencyActiveMixin) {
        await this.$router.push({ path: "/dashboard" });
        return;
      }

      await this.loadCompetencies();
    } catch (error) {
      await this.$router.push({ path: "/dashboard" });
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.requestSequence += 1;
  },
  methods: {
    syncSelectedSort() {
      const currentId = this.selectedSort?.id || "desc";
      this.selectedSort =
          this.sortOptions.find((option) => option.id === currentId) ||
          this.sortOptions[0];
    },
    onSortChange(option) {
      this.selectedSort = option || this.sortOptions[0];
      this.page = 1;
      this.loadCompetencies();
    },
    async loadCompetencies() {
      const requestId = ++this.requestSequence;
      this.loading = true;
      this.errorMessage = "";

      try {
        const response = await ApiService.get(
            `api/my-competencies?${querystring.stringify({
              search: this.searchText.trim(),
              order_by: "created_at",
              sort: this.selectedSort?.id || "desc",
              page: this.page,
              perPage: this.perPage,
            })}`
        );

        if (requestId !== this.requestSequence) return;

        const payload = response?.data || {};
        const meta = payload.meta || {};
        const total = Number(meta.total);

        this.competencies = Array.isArray(payload.data) ? payload.data : [];
        this.totalCompetencies = Number.isFinite(total)
            ? total
            : this.competencies.length;
        this.page = Number(meta.current_page) || this.page;
        this.totalPages = Math.max(
            1,
            Number(meta.last_page) ||
            Math.ceil(this.totalCompetencies / this.perPage) ||
            1
        );
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.competencies = [];
        this.totalCompetencies = 0;
        this.totalPages = 1;
        this.errorMessage =
            error?.message || this.$t("general.try_later");
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    changePage(page) {
      const nextPage = Number(page);
      if (
          !Number.isFinite(nextPage) ||
          nextPage < 1 ||
          nextPage > this.totalPages ||
          nextPage === this.page
      ) {
        return;
      }
      this.page = nextPage;
      this.loadCompetencies();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openCompetency(assignment) {
      if (!assignment?.id) return;
      this.$router.push({
        name: "user.my-development.competency",
        params: { userCompetencyId: String(assignment.id) },
      });
    },
  },
};
</script>

<style scoped>
.development-page {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 8px 0 28px;
  box-sizing: border-box;
  overflow-x: hidden;
}

.development-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-bottom: 18px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
  box-sizing: border-box;
}

.development-toolbar__controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  min-width: 0;
}

.development-search {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  height: 42px;
  padding: 0 12px;
  margin: 0;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  color: #64748b;
  background: #fff;
  box-sizing: border-box;
}

.development-search:focus-within {
  border-color: var(--backgroundColor, #666cff);
  box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.1);
}

.development-search > i {
  flex: 0 0 auto;
  font-size: 17px;
  line-height: 1;
}

.development-search > input[type="search"] {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0;
  margin: 0;
  border: 0;
  outline: 0;
  color: #1e293b;
  background: transparent;
  font: inherit;
  font-size: 14px;
  appearance: none;
  -webkit-appearance: none;
}

.development-search > input[type="search"]::placeholder {
  color: #94a3b8;
  opacity: 1;
}

.development-sort {
  flex: 0 0 230px;
  width: 230px;
  max-width: 230px;
  margin: 0;
  align-self: center;
}

.development-sort :deep(.form-group) {
  margin: 0;
}

.development-sort :deep(.multiselect) {
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
}

.development-sort :deep(.multiselect__tags) {
  box-sizing: border-box;
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
  padding: 8px 40px 0 12px;
  overflow: hidden;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
}

.development-sort :deep(.multiselect__select) {
  height: 40px;
}

.development-sort :deep(.multiselect__placeholder),
.development-sort :deep(.multiselect__single) {
  box-sizing: border-box !important;
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  overflow: hidden !important;
  border: 0 !important;
  background: transparent !important;
  color: #1e293b !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
}

.development-sort :deep(.multiselect__input) {
  position: absolute !important;
  width: 0 !important;
  min-width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}

.development-sort :deep(.multiselect--active .multiselect__tags) {
  border-color: var(--backgroundColor, #666cff);
  box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.1);
}

.development-total {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  padding: 0 14px;
  border: 1px solid #e8edf5;
  border-radius: 8px;
  background: #f8fafc;
  white-space: nowrap;
  box-sizing: border-box;
}

.development-total__label {
  color: #64748b;
  font-size: 13px;
}

.development-total__value {
  color: #1e293b;
  font-size: 18px;
  line-height: 1;
}

.competency-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.development-pagination {
  margin-top: 18px;
}

.development-state {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: #64748b;
  text-align: center;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.development-state > i {
  color: #94a3b8;
  font-size: 34px;
}

.development-state strong {
  display: block;
  color: #334155;
}

.development-state p {
  margin: 4px 0 0;
}

.development-state--error {
  flex-wrap: wrap;
}

.development-state--error > i {
  color: #dc2626;
}

.development-retry {
  padding: 9px 14px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: var(--backgroundColor, #666cff);
  cursor: pointer;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1100px) {
  .competency-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 800px) {
  .development-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .development-toolbar__controls {
    width: 100%;
  }

  .development-search {
    flex: 1 1 auto;
    width: 100%;
  }

  .development-sort {
    flex: 1 1 auto;
    width: auto;
    max-width: none;
    min-width: 0;
  }

  .development-total {
    margin-left: auto;
  }

  .competency-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575px) {
  .development-page {
    padding: 0 0 20px;
  }

  .development-toolbar {
    gap: 10px;
    padding: 12px;
    margin-bottom: 14px;
  }

  .development-toolbar__controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .development-sort {
    width: 100%;
    max-width: none;
  }

  .development-total {
    width: 100%;
    justify-content: space-between;
    margin-left: 0;
  }

  .development-state {
    min-height: 180px;
    flex-direction: column;
    padding: 24px 16px;
  }

  .competency-grid {
    gap: 12px;
  }
}

@media (max-width: 800px) {
  .development-page {
    padding: 0 0 20px;
  }

  .development-page :deep(.page-header-block),
  .development-page :deep(.page-header-block__left),
  .development-page :deep(.page-header-block__title) {
    min-width: 0;
  }

  .development-page :deep(.page-header-block) {
    gap: 8px;
  }

  .development-page :deep(.page-header-block__title) {
    font-size: 18px;
  }

  .development-search,
  .development-sort :deep(.multiselect),
  .development-sort :deep(.multiselect__tags) {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
  }

  .development-search {
    padding: 0 16px;
    border-radius: 10px;
  }

  .development-search > input[type="search"] {
    height: 46px;
    font-size: 16px;
  }

  .development-sort :deep(.multiselect__tags) {
    padding: 12px 40px 12px 16px;
    border-radius: 10px;
  }

  .development-sort :deep(.multiselect__single),
  .development-sort :deep(.multiselect__placeholder) {
    height: 24px !important;
    margin-bottom: 0 !important;
    font-size: 16px !important;
    line-height: 24px !important;
  }

  .development-total {
    min-height: 48px;
    height: 48px;
    padding: 0 16px;
    border-radius: 10px;
  }

  .development-total__label {
    font-size: 14px;
  }

  .development-total__value {
    font-size: 20px;
  }

  .development-state {
    width: 100%;
    box-sizing: border-box;
  }

  .development-retry {
    min-height: 48px;
    padding: 12px 16px;
  }

  .development-pagination {
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>
